import { MultiSelectDropdown } from './MultiSelectDropdown';
import { SingleSelectDropdown } from './SingleSelectDropdown';
import { DateTimeField } from './DateTimeField';
import type { TrainerOption } from '../../types/staff';
import type { WorkshopDayTiming } from '../../utils/workshopSessionUtils';
import {
  MAX_WORKSHOP_DAYS,
  MIN_WORKSHOP_DAYS,
  mergeDayTimings,
} from '../../utils/workshopSessionUtils';

interface WorkshopSessionFieldsProps {
  workshopDays: number;
  onWorkshopDaysChange: (days: number) => void;
  dayTopics: string[][];
  onDayTopicsChange: (dayIndex: number, topics: string[]) => void;
  topicOptions: string[];
  daySchedules: WorkshopDayTiming[];
  onDayScheduleChange: (
    dayIndex: number,
    patch: { startDate?: string; startTime?: string },
  ) => void;
  deliveryTrainerId: string;
  onDeliveryTrainerChange: (id: string) => void;
  deliveryTrainerOptions: TrainerOption[];
  startDate: string;
  startTime: string;
  onStartDateChange: (value: string) => void;
  onStartTimeChange: (value: string) => void;
}

export function WorkshopSessionFields({
  workshopDays,
  onWorkshopDaysChange,
  dayTopics,
  onDayTopicsChange,
  topicOptions,
  daySchedules,
  onDayScheduleChange,
  deliveryTrainerId,
  onDeliveryTrainerChange,
  deliveryTrainerOptions,
  startDate,
  startTime,
  onStartDateChange,
  onStartTimeChange,
}: WorkshopSessionFieldsProps) {
  const timings = mergeDayTimings(
    dayTopics.length,
    startDate,
    startTime,
    daySchedules,
  );

  return (
    <div className="mb-3">
      <div className="mb-3">
        <label className="form-label">Number of workshop days</label>
        <input
          type="number"
          min={MIN_WORKSHOP_DAYS}
          max={MAX_WORKSHOP_DAYS}
          value={workshopDays}
          onChange={(e) => {
            const next = Math.min(
              MAX_WORKSHOP_DAYS,
              Math.max(MIN_WORKSHOP_DAYS, Number(e.target.value) || MIN_WORKSHOP_DAYS),
            );
            onWorkshopDaysChange(next);
          }}
          className="form-control"
        />
      </div>

      <div className="mb-3">
        <DateTimeField
          label="Workshop Day 1 start (other days default from this)"
          date={startDate}
          time={startTime}
          onDateChange={onStartDateChange}
          onTimeChange={onStartTimeChange}
        />
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-warning-emphasis">Day-wise topic planner</h6>
          <p className="small text-muted mb-3">
            Assign topics and a specific date/time for each workshop day.
          </p>
          {dayTopics.map((topics, index) => {
            const takenOnOtherDays = new Set(
              dayTopics.flatMap((day, otherIndex) =>
                otherIndex === index ? [] : day,
              ),
            );
            const availableOptions = topicOptions.filter(
              (option) =>
                !takenOnOtherDays.has(option) || topics.includes(option),
            );
            const timing = timings[index] || {
              dayIndex: index,
              startDate,
              startTime,
            };
            return (
              <div
                key={index}
                className="mb-3 rounded border border-warning-subtle bg-light p-3"
              >
                <label className="form-label fw-semibold">
                  Day {index + 1} topics (multi-select)
                </label>
                <MultiSelectDropdown
                  options={availableOptions.map((option) => ({
                    value: option,
                    label: option,
                  }))}
                  selected={topics}
                  onChange={(next) =>
                    onDayTopicsChange(
                      index,
                      next.filter((topic) => !takenOnOtherDays.has(topic)),
                    )
                  }
                  placeholder="Select topics for this day"
                />
                <div className="mt-3">
                  <DateTimeField
                    label={`Day ${index + 1} date / start time`}
                    date={timing.startDate}
                    time={timing.startTime}
                    onDateChange={(value) =>
                      onDayScheduleChange(index, { startDate: value })
                    }
                    onTimeChange={(value) =>
                      onDayScheduleChange(index, { startTime: value })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">Delivery trainer</label>
        <SingleSelectDropdown
          value={deliveryTrainerId}
          onChange={onDeliveryTrainerChange}
          options={deliveryTrainerOptions.map((trainer) => ({
            value: trainer.id,
            label: trainer.name,
          }))}
          placeholder="Select trainer"
        />
      </div>
    </div>
  );
}
